import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Mail, Phone, MapPin, Send } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

export const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.message) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      toast.error('Please enter a valid email address');
      return;
    }

    setLoading(true);
    
    try {
      // Mock submission
      await new Promise(resolve => setTimeout(resolve, 1000));
      toast.success('Message sent successfully! We\'ll get back to you soon.');
      setFormData({ name: '', email: '', subject: '', message: '' });
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="min-h-screen bg-[#0A0E14] pt-24 md:pt-32 pb-16 md:pb-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
        <Link 
          to="/" 
          className="inline-flex items-center gap-2 text-green-400 hover:text-green-300 mb-8 transition-colors"
        >
          <ArrowLeft size={20} />
          <span className="text-sm">Back to Home</span>
        </Link>

        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">Get in Touch</h1>
          <p className="text-gray-400 text-lg">Have questions? We'd love to hear from you.</p>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          {/* Contact Info */}
          <div className="md:col-span-1 space-y-6">
            <div className="glass-card rounded-xl p-6">
              <div className="w-12 h-12 bg-green-500/10 rounded-lg flex items-center justify-center mb-4">
                <Mail className="text-green-400" size={24} />
              </div>
              <h3 className="text-white font-bold mb-2">Email Us</h3>
              <p className="text-gray-400 text-sm mb-2">Our team is here to help</p>
              <a href="mailto:support@mbhost.in" className="text-green-400 hover:text-green-300 text-sm">
                support@mbhost.in
              </a>
            </div>

            <div className="glass-card rounded-xl p-6">
              <div className="w-12 h-12 bg-green-500/10 rounded-lg flex items-center justify-center mb-4">
                <Phone className="text-green-400" size={24} />
              </div>
              <h3 className="text-white font-bold mb-2">Call Us</h3>
              <p className="text-gray-400 text-sm mb-2">Mon-Fri from 9am to 6pm IST</p>
              <a href="tel:+91XXXXXXXXXX" className="text-green-400 hover:text-green-300 text-sm">
                +91-XXXX-XXXXXX
              </a>
            </div>

            <div className="glass-card rounded-xl p-6">
              <div className="w-12 h-12 bg-green-500/10 rounded-lg flex items-center justify-center mb-4">
                <MapPin className="text-green-400" size={24} />
              </div>
              <h3 className="text-white font-bold mb-2">Visit Us</h3>
              <p className="text-gray-400 text-sm">
                MBhost Cloud Pvt Ltd<br />
                A unit of MBT Ventures Ltd<br />
                Mumbai, India
              </p>
            </div>
          </div>

          {/* Contact Form */}
          <div className="md:col-span-2">
            <form onSubmit={handleSubmit} className="glass-card rounded-2xl p-8 md:p-10">
              <h2 className="text-2xl font-bold text-white mb-6">Send us a Message</h2>
              
              <div className="space-y-5">
                <div>
                  <label className="text-sm text-gray-400 mb-2 block">Name *</label>
                  <Input
                    type="text"
                    placeholder="Your name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="h-12 bg-white/5 border-white/10 text-white placeholder:text-gray-500 focus:border-green-500/50"
                    disabled={loading}
                  />
                </div>

                <div>
                  <label className="text-sm text-gray-400 mb-2 block">Email *</label>
                  <Input
                    type="email"
                    placeholder="your.email@company.com"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="h-12 bg-white/5 border-white/10 text-white placeholder:text-gray-500 focus:border-green-500/50"
                    disabled={loading}
                  />
                </div>

                <div>
                  <label className="text-sm text-gray-400 mb-2 block">Subject</label>
                  <Input
                    type="text"
                    placeholder="How can we help?"
                    value={formData.subject}
                    onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                    className="h-12 bg-white/5 border-white/10 text-white placeholder:text-gray-500 focus:border-green-500/50"
                    disabled={loading}
                  />
                </div>

                <div>
                  <label className="text-sm text-gray-400 mb-2 block">Message *</label>
                  <textarea
                    placeholder="Tell us more about your inquiry..."
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    rows={6}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder:text-gray-500 focus:border-green-500/50 focus:outline-none transition-all resize-none"
                    disabled={loading}
                  />
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full h-12 bg-green-500 hover:bg-green-400 text-black font-semibold transition-all duration-200"
                >
                  {loading ? (
                    'Sending...'
                  ) : (
                    <>
                      Send Message
                      <Send className="ml-2" size={18} />
                    </>
                  )}
                </Button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};